import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Ticket, Plus, Edit, Trash2, Copy, Check, RefreshCw,
  Calendar, Percent, DollarSign, Users, TrendingUp,
  Award, AlertCircle, CheckCircle2, XCircle, Search
} from 'lucide-react';
import { api } from '@/lib/api';
import { ExportButton } from '@/components/ExportButton';

interface Coupon {
  id: number;
  code: string;
  description: string | null;
  discountType: string;
  discountValue: number;
  minOrderAmount: number;
  maxDiscountAmount: number | null;
  usageLimit: number | null;
  usageCount: number;
  perUserLimit: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  applicableTo: string;
  createdAt: string;
  remainingUses: number | null;
  isExpired: boolean;
  isValid: boolean;
}

interface Stats {
  total: number;
  active: number;
  expired: number;
  totalUsages: number;
  totalDiscount: number;
  byType: { type: string; count: number }[];
  topCoupons: { id: number; code: string; usageCount: number; totalDiscount: number }[];
}

export default function CouponsPage() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [copied, setCopied] = useState<number | null>(null);
  const [form, setForm] = useState({
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: 10,
    minOrderAmount: 0,
    maxDiscountAmount: null as number | null,
    usageLimit: null as number | null,
    perUserLimit: 1,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isActive: true,
    applicableTo: 'all'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [couponsRes, statsRes] = await Promise.all([
        api.get('/api/coupons?pageSize=50'),
        api.get('/api/coupons/stats')
      ]);

      if (couponsRes.data.success) setCoupons(couponsRes.data.data.items);
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    if (!form.code) {
      alert('يرجى إدخال كود الكوبون');
      return;
    }
    try {
      const res = await api.post('/api/coupons', form);
      if (res.data.success) {
        alert('تم إنشاء الكوبون بنجاح');
        setShowCreateModal(false);
        resetForm();
        loadData();
      } else {
        alert(res.data.message || 'حدث خطأ');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ');
    }
  };

  const updateCoupon = async () => {
    if (!editingCoupon) return;
    try {
      const res = await api.put(`/api/coupons/${editingCoupon.id}`, form);
      if (res.data.success) {
        alert('تم تحديث الكوبون بنجاح');
        setEditingCoupon(null);
        resetForm();
        loadData();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ');
    }
  };

  const deleteCoupon = async (id: number) => {
    if (!confirm('هل أنت متأكد من حذف هذا الكوبون؟')) return;
    try {
      const res = await api.delete(`/api/coupons/${id}`);
      if (res.data.success) loadData();
    } catch (error) {
      console.error('Error deleting coupon:', error);
    }
  };

  const toggleActive = async (coupon: Coupon) => {
    try {
      await api.put(`/api/coupons/${coupon.id}`, { isActive: !coupon.isActive });
      loadData();
    } catch (error) {
      console.error('Error toggling coupon:', error);
    }
  };

  const copyCode = (code: string, id: number) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const resetForm = () => {
    setForm({
      code: '',
      description: '',
      discountType: 'percentage',
      discountValue: 10,
      minOrderAmount: 0,
      maxDiscountAmount: null,
      usageLimit: null,
      perUserLimit: 1,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: true,
      applicableTo: 'all'
    });
  };

  const openEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setForm({
      code: coupon.code,
      description: coupon.description || '',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minOrderAmount: coupon.minOrderAmount,
      maxDiscountAmount: coupon.maxDiscountAmount,
      usageLimit: coupon.usageLimit,
      perUserLimit: coupon.perUserLimit,
      startDate: new Date(coupon.startDate).toISOString().split('T')[0],
      endDate: new Date(coupon.endDate).toISOString().split('T')[0],
      isActive: coupon.isActive,
      applicableTo: coupon.applicableTo
    });
    setShowCreateModal(true);
  };

  const filteredCoupons = coupons.filter(c => {
    if (filter === 'active' && !c.isValid) return false;
    if (filter === 'expired' && !c.isExpired) return false;
    if (filter === 'disabled' && c.isActive) return false;
    if (search && !c.code.toLowerCase().includes(search.toLowerCase()) && 
        !c.description?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    return true;
  });

  const formatCurrency = (value: number) => `${value.toLocaleString('ar-EG', { maximumFractionDigits: 2 })} ج.م`;

  const exportColumns = [
    { key: 'code', label: 'الكود' },
    { key: 'description', label: 'الوصف' },
    { key: 'discountType', label: 'نوع الخصم' },
    { key: 'discountValue', label: 'قيمة الخصم' },
    { key: 'usageCount', label: 'عدد الاستخدام' },
    { key: 'isActive', label: 'نشط', format: (v: boolean) => v ? 'نعم' : 'لا' },
    { key: 'endDate', label: 'تاريخ الانتهاء', format: (v: string) => new Date(v).toLocaleDateString('ar-EG') }
  ];

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Ticket className="w-6 h-6 text-white" />
            </div>
            إدارة الكوبونات
          </h1>
          <p className="text-muted-foreground mt-1">إنشاء وإدارة الكوبونات والعروض الخاصة</p>
        </div>
        <div className="flex gap-2">
          <ExportButton data={coupons} filename="coupons" columns={exportColumns} title="الكوبونات" />
          <Button onClick={() => { resetForm(); setEditingCoupon(null); setShowCreateModal(true); }} 
                  className="bg-indigo-600 hover:bg-indigo-700 gap-2">
            <Plus className="w-4 h-4" />
            كوبون جديد
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">الإجمالي</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                </div>
                <Ticket className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">نشط</p>
                  <p className="text-2xl font-bold text-green-900">{stats.active}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">منتهي</p>
                  <p className="text-2xl font-bold text-red-900">{stats.expired}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">الاستخدامات</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.totalUsages}</p>
                </div>
                <Users className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-700">إجمالي الخصومات</p>
                  <p className="text-lg font-bold text-amber-900">{formatCurrency(stats.totalDiscount)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="بحث عن كوبون..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-10"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg bg-white"
            >
              <option value="all">الكل</option>
              <option value="active">نشط</option>
              <option value="expired">منتهي</option>
              <option value="disabled">معطل</option>
            </select>
            <Button onClick={loadData} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ml-2 ${loading ? 'animate-spin' : ''}`} />
              تحديث
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Coupons Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCoupons.map((coupon) => (
          <Card key={coupon.id} className={`relative overflow-hidden ${!coupon.isValid ? 'opacity-60' : ''}`}>
            <div className={`absolute top-0 left-0 right-0 h-1 ${
              coupon.isValid ? 'bg-gradient-to-r from-indigo-500 to-purple-600' : 
              coupon.isExpired ? 'bg-red-500' : 'bg-gray-400'
            }`} />
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold font-mono text-indigo-600">{coupon.code}</h3>
                    <Button size="sm" variant="ghost" onClick={() => copyCode(coupon.code, coupon.id)} className="h-6 w-6 p-0">
                      {copied === coupon.id ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-1">{coupon.description || 'بدون وصف'}</p>
                </div>
                <Badge className={coupon.isValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {coupon.isValid ? 'نشط' : coupon.isExpired ? 'منتهي' : 'معطل'}
                </Badge>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {coupon.discountType === 'percentage' ? 
                      <Percent className="w-5 h-5 text-indigo-600" /> : 
                      <DollarSign className="w-5 h-5 text-indigo-600" />
                    }
                    <span className="text-2xl font-bold text-indigo-900">
                      {coupon.discountValue}{coupon.discountType === 'percentage' ? '%' : ' ج.م'}
                    </span>
                  </div>
                  {coupon.maxDiscountAmount && (
                    <span className="text-xs text-gray-600">حد أقصى: {formatCurrency(coupon.maxDiscountAmount)}</span>
                  )}
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">الاستخدامات:</span>
                  <span className="font-semibold">
                    {coupon.usageCount}{coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
                  </span>
                </div>
                {coupon.usageLimit && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min((coupon.usageCount / coupon.usageLimit) * 100, 100)}%` }}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">الحد الأدنى:</span>
                  <span className="font-semibold">{formatCurrency(coupon.minOrderAmount)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">تاريخ الانتهاء:</span>
                  <span className="font-semibold flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(coupon.endDate).toLocaleDateString('ar-EG')}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 mt-4 pt-4 border-t">
                <Button size="sm" variant="outline" onClick={() => openEdit(coupon)} className="flex-1">
                  <Edit className="w-3 h-3 ml-1" />
                  تعديل
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleActive(coupon)} 
                        className={coupon.isActive ? 'text-orange-600' : 'text-green-600'}>
                  {coupon.isActive ? 'تعطيل' : 'تفعيل'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => deleteCoupon(coupon.id)} className="text-red-600">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCoupons.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Ticket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">لا توجد كوبونات</p>
          </CardContent>
        </Card>
      )}

      {/* Top Coupons */}
      {stats && stats.topCoupons.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              أكثر الكوبونات استخداماً
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.topCoupons.map((coupon, index) => (
                <div key={coupon.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-600 text-white' :
                    index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                    index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-mono font-bold text-indigo-600">{coupon.code}</p>
                    <p className="text-xs text-gray-500">{coupon.usageCount} استخدام</p>
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-gray-500">إجمالي الخصومات</p>
                    <p className="font-bold text-purple-600">{formatCurrency(coupon.totalDiscount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingCoupon ? 'تحديث الكوبون' : 'إنشاء كوبون جديد'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">كود الكوبون *</label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="WELCOME10"
                    className="font-mono"
                    disabled={!!editingCoupon}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">نوع الخصم</label>
                  <select
                    value={form.discountType}
                    onChange={(e) => setForm({ ...form, discountType: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="percentage">نسبة مئوية (%)</option>
                    <option value="fixed">مبلغ ثابت (ج.م)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">الوصف</label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="وصف الكوبون"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    قيمة الخصم {form.discountType === 'percentage' ? '%' : 'ج.م'}
                  </label>
                  <Input
                    type="number"
                    value={form.discountValue}
                    onChange={(e) => setForm({ ...form, discountValue: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الحد الأدنى للطلب (ج.م)</label>
                  <Input
                    type="number"
                    value={form.minOrderAmount}
                    onChange={(e) => setForm({ ...form, minOrderAmount: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">الحد الأقصى للخصم (ج.م) - اختياري</label>
                  <Input
                    type="number"
                    value={form.maxDiscountAmount ?? ''}
                    onChange={(e) => setForm({ ...form, maxDiscountAmount: e.target.value ? Number(e.target.value) : null })}
                    placeholder="بدون حد"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">حد الاستخدامات - اختياري</label>
                  <Input
                    type="number"
                    value={form.usageLimit ?? ''}
                    onChange={(e) => setForm({ ...form, usageLimit: e.target.value ? Number(e.target.value) : null })}
                    placeholder="بدون حد"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">تاريخ البداية</label>
                  <Input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">تاريخ الانتهاء</label>
                  <Input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">حد الاستخدام لكل مستخدم</label>
                  <Input
                    type="number"
                    value={form.perUserLimit}
                    onChange={(e) => setForm({ ...form, perUserLimit: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="isActive" className="text-sm font-medium">نشط</label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={editingCoupon ? updateCoupon : createCoupon} 
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                  {editingCoupon ? 'تحديث' : 'إنشاء'}
                </Button>
                <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingCoupon(null); }} className="flex-1">
                  إلغاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
